import "#app/global.css";

import { ThemeProvider } from "@salestrak/ui";
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";

import { AuthProvider, useAuth } from "#app/lib/auth-context";
import { SplashDoneProvider } from "#app/lib/splash-context";
import { SplashScreen } from "#app/components/brand/splash-screen";
import { AppProviders } from "#app/lib/app-providers";
import { AppErrorBoundary } from "#app/lib/route-error-boundary";
import { ImpersonationBanner } from "#app/components/impersonation-banner";
import { useVersionCheck } from "#app/lib/use-version-check";
import { useHasStandardAppearance } from "#app/lib/hooks/use-has-standard-appearance";
import { devOnly } from "#app/lib/dev-only";

/** `?layoutDebug=1` geometry readout; absent from the production bundle. */
const LayoutDebugOverlay = devOnly(() =>
  import("#app/components/dev/layout-debug").then((m) => ({
    default: m.LayoutDebugOverlay,
  })),
);

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /**
     * Set on an (auth) screen that works for an ALREADY-SIGNED-IN user, which
     * suppresses the bounce into the app below. The emailed set-password link
     * is the case that needs it: it is requested from inside the app, so the
     * session that asked for it is exactly the one that opens it. The flag
     * lives on the route rather than in a path list here for the same reason
     * `inAuthGroup` reads route ids — a list in this file silently bounced
     * every screen it didn't know about.
     */
    allowAuthenticated?: boolean;
    /**
     * Set on a screen that wears the SHIPPED appearance — default palette and
     * accent, the device's light/dark — instead of the user's: the `(auth)`
     * group, the workspace picker and create screens, admin. Those belong to
     * no user or no workspace yet, and the appearance mirror is per browser,
     * so without this the sign-in screen wore the last visitor's theme. Read
     * by `useHasStandardAppearance`; the `ThemeProvider` mode override and
     * `ThemeAppearanceProvider` both key on it.
     */
    standardAppearance?: boolean;
  }
}

/** A path on this site worth coming back to after log in, or nothing. */
function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return undefined;
  }
  const path = value.split(/[?#]/)[0] ?? "";
  return path === "/" || path === "/logout" || path === "/login" ? undefined : value;
}

/**
 * Inner navigator with access to auth context.
 * Redirects between (auth) and (app) route groups based on auth state.
 */
function RootNavigator() {
  const { state } = useAuth();
  // Membership in the (auth) group comes from the matched route ids, not a
  // path list — a hardcoded list here silently bounced every auth screen it
  // didn't know about back to /login the moment one was added.
  const inAuthGroup = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId.startsWith("/(auth)")),
  });
  // Deliberately a SECOND flag rather than an exclusion from `inAuthGroup`:
  // these screens still belong to the group, so an unauthenticated visitor
  // must be left on them instead of being sent to /login.
  const allowsAuthenticated = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.allowAuthenticated === true),
  });
  const navigate = useNavigate();
  const router = useRouter();
  const href = useRouterState({ select: (s) => s.location.href });
  // The location reaches /login a moment before the route matches do; without
  // this guard that moment would send the user to /login again, around itself.
  const onLogin = useRouterState({ select: (s) => s.location.pathname === "/login" });
  const redirect = useRouterState({
    select: (s) => safeRedirect(s.location.search.redirect),
  });
  // A visit with no session keeps the page it asked for (a bookmark, a link),
  // so log in lands there. A sign-out keeps nothing: the next person to log
  // in on this machine can be someone else.
  const wasAuthenticated = React.useRef(state.isAuthenticated);
  // One move out of the auth screens per visit to them. The route matches
  // reach the new page a moment after the location does; in that moment the
  // screen still reads as an auth screen, and a second move would drop the
  // page the first one went to.
  const leftAuthScreens = React.useRef(false);

  useVersionCheck();

  React.useEffect(() => {
    const before = wasAuthenticated.current;
    wasAuthenticated.current = state.isAuthenticated;
    if (state.isAuthenticated === null) return;

    if (!inAuthGroup) leftAuthScreens.current = false;
    if (state.isAuthenticated && inAuthGroup && !allowsAuthenticated) {
      if (leftAuthScreens.current) return;
      leftAuthScreens.current = true;
      if (redirect) router.history.push(redirect);
      else navigate({ to: "/" });
    } else if (!state.isAuthenticated && !inAuthGroup && !onLogin) {
      // The navigator mounts after the splash, when the first check may
      // already say "signed out"; only a sign-out (true before) drops the page.
      const keep = before === true ? undefined : safeRedirect(href);
      navigate({ to: "/login", search: keep ? { redirect: keep } : {} });
    }
  }, [state.isAuthenticated, inAuthGroup, allowsAuthenticated, navigate, router, href, redirect, onLogin]);

  return <Outlet />;
}

const SPLASH_MIN_MS = 1000;

function RootComponent() {
  const [splashDone, setSplashDone] = React.useState(false);
  // On a standard-appearance screen the document follows the DEVICE, not the
  // stored light/dark choice: `themeMode` is a user preference like the
  // accent, and the sign-in and picker screens have no user — or no space —
  // whose choice should apply. Entering the workspace lifts the override, and
  // the user's mode lands with the rest of their appearance. Same line as
  // ThemeAppearanceProvider draws for the accent and palette.
  const standardAppearance = useHasStandardAppearance();

  React.useEffect(() => {
    const timer = setTimeout(() => setSplashDone(true), SPLASH_MIN_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    // #root is a full-height flex column via global.css, so no wrapper
    // element is needed here. `storageKey="theme_mode"` keeps every existing
    // user's stored light|dark|system value working.
    <AuthProvider>
      {/* The screens render live under the splash overlay, so anything that
          would grab focus (and with it Chrome's autofill dropdown, which
          paints above the overlay) waits on this via useSplashAutoFocus. */}
      <SplashDoneProvider value={splashDone}>
        <ThemeProvider
          defaultTheme="dark"
          storageKey="theme_mode"
          forcedTheme={standardAppearance ? "system" : undefined}
        >
          <AppProviders>
            <ImpersonationBanner />
            <AppErrorBoundary>
              <RootNavigator />
            </AppErrorBoundary>
          </AppProviders>
          <LayoutDebugOverlay />
        </ThemeProvider>
      </SplashDoneProvider>

      {!splashDone && <SplashScreen />}
    </AuthProvider>
  );
}

export const Route = createRootRoute({ component: RootComponent });
